import { useState } from 'react';
import { fetchWithAuth } from '../../../utils/apiAuth';
import config from '../../../config';

const apiBaseUrl = config.getApiUrl();

const emptyQuestion = (id = 1) => ({
  id,
  question: '',
  options: ['', '', '', ''],
  correctAnswer: 0,
  images: []
});

const emptyQuizForm = () => ({
  name: '',
  banner: '',
  isActive: true,
  isFavourite: false,
  questions: [emptyQuestion()]
});

export const useQuizzes = (showSnackbar) => {
  const [quizForm, setQuizForm] = useState(emptyQuizForm);
  const [isCreatingQuiz, setIsCreatingQuiz] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [quizzesError, setQuizzesError] = useState(null);
  const [editingQuizId, setEditingQuizId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState(null);
  const [showQuizCreator, setShowQuizCreator] = useState(false);

  const fetchQuizzes = async () => {
    try {
      setQuizzesLoading(true);
      setQuizzesError(null);
      const response = await fetchWithAuth(`${apiBaseUrl}/api/quizzes`);
      setQuizzes(response.data || response || []);
    } catch (err) {
      console.error('Error fetching quizzes:', err);
      setQuizzesError(err.message);
    } finally {
      setQuizzesLoading(false);
    }
  };

  const handleQuizFormChange = (field) => (event) => {
    setQuizForm((prev) => ({
      ...prev,
      [field]: (field === 'isActive' || field === 'isFavourite') ? event.target.checked : event.target.value
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...quizForm.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value
    };
    setQuizForm({ ...quizForm, questions: updatedQuestions });
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...quizForm.questions];
    updatedQuestions[questionIndex].options[optionIndex] = value;
    setQuizForm({ ...quizForm, questions: updatedQuestions });
  };

  const handleAddQuestion = () => {
    setQuizForm((prev) => ({
      ...prev,
      questions: [...prev.questions, emptyQuestion(prev.questions.length + 1)]
    }));
  };

  const handleRemoveQuestion = (index) => {
    if (quizForm.questions.length > 1) {
      const updatedQuestions = quizForm.questions.filter((_, i) => i !== index);
      setQuizForm({ ...quizForm, questions: updatedQuestions });
    }
  };

  const handleAddImage = (questionIndex) => {
    const updatedQuestions = [...quizForm.questions];
    if (!updatedQuestions[questionIndex].images) {
      updatedQuestions[questionIndex].images = [];
    }
    updatedQuestions[questionIndex].images.push('');
    setQuizForm({ ...quizForm, questions: updatedQuestions });
  };

  const handleImageChange = (questionIndex, imageIndex, value) => {
    const updatedQuestions = [...quizForm.questions];
    updatedQuestions[questionIndex].images[imageIndex] = value;
    setQuizForm({ ...quizForm, questions: updatedQuestions });
  };

  const handleRemoveImage = (questionIndex, imageIndex) => {
    const updatedQuestions = [...quizForm.questions];
    updatedQuestions[questionIndex].images = updatedQuestions[questionIndex].images.filter((_, i) => i !== imageIndex);
    setQuizForm({ ...quizForm, questions: updatedQuestions });
  };

  const validateQuizForm = () => {
    if (!quizForm.name) {
      showSnackbar('Quiz name is required', 'error');
      return false;
    }

    if (quizForm.questions.length === 0) {
      showSnackbar('At least one question is required', 'error');
      return false;
    }

    for (let i = 0; i < quizForm.questions.length; i++) {
      const q = quizForm.questions[i];
      if (!q.question.trim()) {
        showSnackbar(`Question ${i + 1} text is required`, 'error');
        return false;
      }
      if (q.options.some(opt => !opt.trim())) {
        showSnackbar(`All options for Question ${i + 1} must be filled`, 'error');
        return false;
      }
    }

    return true;
  };

  const handleCreateQuiz = async () => {
    try {
      setIsCreatingQuiz(true);

      if (!validateQuizForm()) return;

      const url = editingQuizId
        ? `${apiBaseUrl}/api/quizzes/${editingQuizId}`
        : `${apiBaseUrl}/api/quizzes`;

      const response = await fetchWithAuth(url, {
        method: editingQuizId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(quizForm)
      });

      if (response.success) {
        showSnackbar(editingQuizId ? 'Quiz updated successfully!' : 'Quiz created successfully!', 'success');

        setQuizForm(emptyQuizForm());
        setEditingQuizId(null);
        setShowQuizCreator(false);
        fetchQuizzes();
      }
    } catch (err) {
      console.error('Error creating quiz:', err);
      showSnackbar(
        err.message || (editingQuizId ? 'Failed to update quiz. Please try again.' : 'Failed to create quiz. Please try again.'),
        'error'
      );
    } finally {
      setIsCreatingQuiz(false);
    }
  };

  const handleEditQuiz = (quiz) => {
    setEditingQuizId(quiz._id);
    setShowQuizCreator(true);
    setQuizForm({
      name: quiz.name,
      banner: quiz.banner || '',
      isActive: quiz.isActive,
      isFavourite: quiz.isFavourite || false,
      questions: quiz.questions.map((q, idx) => ({
        id: idx + 1,
        question: q.question,
        options: q.options,
        correctAnswer: q.correctAnswer,
        images: q.images || []
      }))
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingQuizId(null);
    setShowQuizCreator(false);
    setQuizForm(emptyQuizForm());
  };

  const handleDeleteQuiz = (quiz) => {
    setQuizToDelete(quiz);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteQuiz = async () => {
    try {
      const response = await fetchWithAuth(`${apiBaseUrl}/api/quizzes/${quizToDelete._id}`, {
        method: 'DELETE'
      });

      if (response.success) {
        showSnackbar('Quiz deleted successfully!', 'success');
        fetchQuizzes();
      }
    } catch (err) {
      console.error('Error deleting quiz:', err);
      showSnackbar('Failed to delete quiz. Please try again.', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setQuizToDelete(null);
    }
  };

  const cancelDeleteQuiz = () => {
    setDeleteDialogOpen(false);
    setQuizToDelete(null);
  };

  return {
    quizForm,
    isCreatingQuiz,
    quizzes,
    quizzesLoading,
    quizzesError,
    editingQuizId,
    deleteDialogOpen,
    quizToDelete,
    showQuizCreator,
    setShowQuizCreator,
    fetchQuizzes,
    handleQuizFormChange,
    handleQuestionChange,
    handleOptionChange,
    handleAddQuestion,
    handleRemoveQuestion,
    handleAddImage,
    handleImageChange,
    handleRemoveImage,
    handleCreateQuiz,
    handleEditQuiz,
    handleCancelEdit,
    handleDeleteQuiz,
    confirmDeleteQuiz,
    cancelDeleteQuiz
  };
};
